import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { isValidEmail } from '@lender/utils';
import { Box, Button, Switch, Typography } from '@mui/material';
import DrawerCDS, { DrawerSize } from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import { applicationBorrowerApi } from 'app/services/api';
import PropTypes from 'prop-types';

import { showErrorMessageAction, showMessageAction } from 'store/global/actions';
import { getBorrowersTableDataAction } from 'store/loanApplications/actions';

import {
  INVITE_CO_BORROWER_DRAWER_TITLE,
  INVITE_CO_BORROWER_EMAIL_ERROR_MESSAGE,
  INVITE_CO_BORROWER_ERROR_MESSAGE,
  INVITE_CO_BORROWER_FORM_ID,
  INVITE_CO_BORROWER_JOINT_HELPER_TEXT,
  INVITE_CO_BORROWER_SEPARATE_APPLICATION_LABEL,
  INVITE_CO_BORROWER_SEPARATE_HELPER_TEXT,
  INVITE_CO_BORROWER_SUBMIT_BUTTON_LABEL,
  INVITE_CO_BORROWER_SUCCESS_MESSAGE,
  InviteCoBorrowerDrawerTestIds,
} from './InviteCoBorrowerDrawer.constants';
import { drawerBodyStyles, separateApplicationRowStyles } from './InviteCoBorrowerDrawer.styles';

const InviteCoBorrowerDrawer = ({ open, onClose, applicationId, borrower }) => {
  const dispatch = useDispatch();

  const [email, setEmail] = useState('');
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const [createSeparateApplication, setCreateSeparateApplication] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmedEmail = email.trim();
  const hasEmailError = isEmailTouched && !isValidEmail(email);
  const isDirty = !!trimmedEmail || createSeparateApplication;
  const canInvite = !isSubmitting && isValidEmail(email);

  const resetForm = useCallback(() => {
    setEmail('');
    setIsEmailTouched(false);
    setCreateSeparateApplication(false);
  }, []);

  const onEmailChange = useCallback(event => {
    setEmail(event.target.value);
    setIsEmailTouched(true);
  }, []);

  const onCreateSeparateApplicationChange = useCallback(
    event => setCreateSeparateApplication(event.target.checked),
    [],
  );

  const onDrawerClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      if (!canInvite) {
        return;
      }

      setIsSubmitting(true);

      applicationBorrowerApi
        .inviteCoBorrower({
          applicationId,
          borrowerId: borrower.id,
          email: trimmedEmail,
          createSeparateApplication,
        })
        .then(() => {
          dispatch(
            showMessageAction({ message: INVITE_CO_BORROWER_SUCCESS_MESSAGE, variant: 'success' }),
          );
          resetForm();
          onClose();
          dispatch(getBorrowersTableDataAction({ applicationId }));
        })
        .catch(err => {
          dispatch(showErrorMessageAction({ message: INVITE_CO_BORROWER_ERROR_MESSAGE, err }));
        })
        .finally(() => setIsSubmitting(false));
    },
    [
      canInvite,
      applicationId,
      borrower.id,
      trimmedEmail,
      createSeparateApplication,
      dispatch,
      resetForm,
      onClose,
    ],
  );

  const content = (
    <Box
      noValidate
      component="form"
      id={INVITE_CO_BORROWER_FORM_ID}
      sx={drawerBodyStyles}
      onSubmit={onSubmit}
    >
      <Typography variant="regular14">
        Send {borrower.fullName} an invitation to complete their part of this loan application.
      </Typography>

      <TextFieldCDS
        required
        dataTestId={InviteCoBorrowerDrawerTestIds.EMAIL_INPUT}
        error={hasEmailError}
        helperText={hasEmailError ? INVITE_CO_BORROWER_EMAIL_ERROR_MESSAGE : ''}
        label="Email"
        name="email"
        placeholder="name@company.com"
        value={email}
        onChange={onEmailChange}
      />

      <Box>
        <Box sx={separateApplicationRowStyles}>
          <Switch
            checked={createSeparateApplication}
            data-testid={InviteCoBorrowerDrawerTestIds.SEPARATE_APPLICATION_SWITCH}
            disabled={isSubmitting}
            name="createSeparateApplication"
            size="small"
            onChange={onCreateSeparateApplicationChange}
          />
          <Typography variant="medium14">
            {INVITE_CO_BORROWER_SEPARATE_APPLICATION_LABEL}
          </Typography>
        </Box>
        <Typography color="text.secondary" variant="regular12">
          {createSeparateApplication
            ? INVITE_CO_BORROWER_SEPARATE_HELPER_TEXT
            : INVITE_CO_BORROWER_JOINT_HELPER_TEXT}
        </Typography>
      </Box>
    </Box>
  );

  const actions = (
    <Button
      data-testid={InviteCoBorrowerDrawerTestIds.SUBMIT_BUTTON}
      disabled={!canInvite}
      form={INVITE_CO_BORROWER_FORM_ID}
      type="submit"
      variant="contained"
    >
      {INVITE_CO_BORROWER_SUBMIT_BUTTON_LABEL}
    </Button>
  );

  return (
    <DrawerCDS
      actions={actions}
      content={content}
      dataTestId={InviteCoBorrowerDrawerTestIds.DRAWER}
      isDirty={isDirty}
      isLoading={isSubmitting}
      open={open}
      size={DrawerSize.SMALL}
      title={INVITE_CO_BORROWER_DRAWER_TITLE}
      onClose={onDrawerClose}
    />
  );
};

InviteCoBorrowerDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  applicationId: PropTypes.string.isRequired,
  borrower: PropTypes.shape({
    id: PropTypes.string,
    fullName: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default InviteCoBorrowerDrawer;
